import React, { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import voucherServices from '../voucherServices';
import { useAppData } from '../../../../../../context/AppDataContext';
import { useReactToPrint } from 'react-to-print';
import { CancelOutlined, CheckCircle, Print, Save, ReceiptLong } from '@mui/icons-material';
import { CircularProgress, Dialog, DialogContent } from '@mui/material';
import { useAlert } from '../../../../../../context/AlertContext';

// Reusing your existing sections
import SalesVoucherHeader from '../section/SalesVoucherHeader';
import PurchaseVoucherHeader from '../section/PurchaseVoucherHeader';
import VoucherFooter from '../section/VoucherFooter';

// We will create a specialized Item List for Notes next
import CDNoteItemList from '../section/CDNoteItemList'; 
import FinanceLayout from '../../../../../../layout/components/FinanceLayout';

function CreditDebitNoteVoucherDetailsPage() {
    const { id } = useParams();
    const printComponentRef = useRef();
    const { AppAlert } = useAlert();

    const [dialogStatus, setDialogStatus] = useState(false);
    const [companyDetails, setCompanyDetails] = useState(null);
    const [partyDetails, setPartyDetails] = useState(null);
    const [voucherDetails, setVoucherDetails] = useState(null);
    const { productList, getProductList } = useAppData();
    const [isPrinting, setIsPrinting] = useState(false);

    const getVoucherDetails = () => {
        voucherServices.getById(id).then(res => {
            setVoucherDetails(res.data);
        }).catch(error => {
            console.log(error);
            AppAlert("Error loading voucher details", "error");
        });
    }

    useEffect(() => {
        getVoucherDetails();
        getProductList();
    }, [id]);

    const finalizeNote = () => {
        setDialogStatus(true);
        // We will create this service method in the next step
        voucherServices.cdNoteFinalize(id).then(res => {
            AppAlert(res.msg);
            getVoucherDetails();
        }).catch(error => {
            AppAlert(error.msg || "Finalization Failed", "error");
        }).finally(() => {
            setDialogStatus(false);
        });
    }

    const discardNote = () => {
        setDialogStatus(true);
        voucherServices.cdNoteDiscard(id).then(res => {
            AppAlert(res.msg);
            getVoucherDetails();
        }).catch(error => {
            AppAlert(error.msg, "error");
        }).finally(() => {
            setDialogStatus(false);
        });
    }

    const handlePrint = useReactToPrint({
        content: () => printComponentRef.current,
        onBeforeGetContent: () => setIsPrinting(true),
        onAfterPrint: () => setIsPrinting(false),
    });

    if (!voucherDetails) return <FinanceLayout><div className='text-center mt-5'><CircularProgress /></div></FinanceLayout>;

    const isCreditNote = voucherDetails.voucherType === "Credit Note";

    return (
            <FinanceLayout>
            <div className='container'>
                {/* Loader Dialog */}
                <Dialog open={dialogStatus} fullWidth maxWidth="sm">
                    <DialogContent className='text-center p-5'>
                        <CircularProgress />
                        <div className='mt-2 fw-bold'>Processing {voucherDetails.voucherType}...</div>
                    </DialogContent>
                </Dialog>

                <div className='row mb-3'>
                    <div className='col-md-12 d-flex align-items-center justify-content-between'>
                        <h5 className='mb-0'>
                            <ReceiptLong className='me-2' /> 
                            {voucherDetails.voucherType} Details 
                            <span className='badge bg-light text-dark ms-2 border'>Ref Invoice: #{voucherDetails.referenceId}</span>
                        </h5>
                        
                        <div>
                            <button className='btn btn-outline-primary me-2' onClick={handlePrint}><Print /> Print</button>
                            
                            {voucherDetails.status === 1 && (
                                <button className='btn btn-primary' onClick={finalizeNote}>
                                    <CheckCircle /> Finalize & Update Stock
                                </button>
                            )}

                            {voucherDetails.status === 2 && (
                                <>
                                    <span className='badge bg-success p-2 me-2'><CheckCircle /> Finalized</span>
                                    <button className='btn btn-outline-danger' onClick={discardNote}><CancelOutlined /> Discard</button>
                                </>
                            )}

                            {voucherDetails.status === 3 && (
                                <span className='badge bg-danger p-2'><CancelOutlined /> Cancelled</span>
                            )}
                        </div>
                    </div>
                </div>

                <div ref={printComponentRef} className='p-3 bg-white shadow-sm border rounded'>
                    <div className='row'>
                        <div className='col-md-12'>
                            {/* Reusing existing Headers - Logic determines if it's a Sales/Purchase style based on Note Type */}
                            {isCreditNote ? (
                                <SalesVoucherHeader 
                                    voucherDetails={voucherDetails}
                                    companyDetails={companyDetails} setCompanyDetails={setCompanyDetails}
                                    partyDetails={partyDetails} setPartyDetails={setPartyDetails}
                                    callBack={getVoucherDetails}
                                />
                            ) : (
                                <PurchaseVoucherHeader 
                                    voucherDetails={voucherDetails}
                                    companyDetails={companyDetails} setCompanyDetails={setCompanyDetails}
                                    partyDetails={partyDetails} setPartyDetails={setPartyDetails}
                                    callBack={getVoucherDetails}
                                />
                            )}
                        </div>

                        <div className='col-md-12 mt-3'>
                            {/* NEW: Component to handle selecting items from the original invoice */}
                            
                            <CDNoteItemList 
                                voucherDetails={voucherDetails}
                                productList={productList}
                                isPrinting={isPrinting}
                                callBack={getVoucherDetails}
                            />
                        </div>

                        <div className='col-md-12 mt-4'>
                            <VoucherFooter 
                                voucherDetails={voucherDetails}
                                companyDetails={companyDetails}
                                partyDetails={partyDetails}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </FinanceLayout>
    )
}

export default CreditDebitNoteVoucherDetailsPage;